/**
 * RÝCHLE HĽADANIE NA TELEFÓNE (.466).
 *
 * Oto: „Jedno pole hore, kam napíšem kód tovaru, číslo objednávky, firmu či
 * telefón a systém ma hodí na správne miesto."
 *
 * ── Prečo je to len na telefóne ──────────────────────────────────────────
 * Pri počítači sa hľadá v sekcii: je tam filter, stĺpce a celá obrazovka.
 * Na telefóne je cesta do sekcie dlhá (hamburger, zoznam, obrazovka, filter)
 * a človek pri pulte často ani nevie, v ktorej sekcii to hľadané je – vie len
 * číslo, ktoré má pred sebou. Preto lupa v hornej lište, ktorá je na telefóne
 * aj tak takmer prázdna.
 *
 * ── Čo sa tu stráži ──────────────────────────────────────────────────────
 *  • **Nehľadá sa po každom písmene.** Čaká sa, kým človek prestane písať;
 *    inak by pri číslach objednávok odišlo osem dopytov za sebou a odpovede
 *    by prichádzali v inom poradí, než sa pýtali.
 *  • **Výsledok sa prepíše len vtedy, keď patrí k tomu, čo je napísané.**
 *    Pomalšia odpoveď na kratší dopyt inak prekryje tú správnu.
 *  • **Kto na sekciu nevidí, nedostane z nej výsledok** – rozhoduje o tom
 *    server, nie táto obrazovka.
 *  • **Prázdno sa nemlčí.** Keď sa nenájde nič, je to napísané; prázdna
 *    obrazovka vyzerá ako pokazené hľadanie.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Phone, ChevronRight } from 'lucide-react';
import { api } from '../api';

export default function RychleHladanie({ open, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [v, setV] = useState(null);
  const [hladam, setHladam] = useState(false);
  const pole = useRef(null);
  /* Posledný dopyt, ktorého odpoveď sa smie zobraziť. */
  const platny = useRef('');

  useEffect(() => {
    if (!open) { setQ(''); setV(null); return undefined; }
    /* Klávesnica sa má otvoriť sama – kto ťukne na lupu, ide písať. */
    const t = setTimeout(() => pole.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const dopyt = q.trim();
    platny.current = dopyt;
    if (dopyt.length < 2) { setV(null); setHladam(false); return undefined; }
    setHladam(true);
    const t = setTimeout(async () => {
      try {
        const d = await api.get(`/api/hladanie?q=${encodeURIComponent(dopyt)}`);
        /* Kým odpoveď chodila, človek mohol dopísať ďalšie písmená. Stará
         * odpoveď by prekryla novú a vyzeralo by to ako náhodné výsledky. */
        if (platny.current !== dopyt) return;
        setV(d);
      } catch {
        if (platny.current === dopyt) setV({ skupiny: [], spolu: 0, chyba: true });
      } finally {
        if (platny.current === dopyt) setHladam(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;

  const chod = (odkaz) => { onClose(); nav(odkaz); };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
      <div className="flex items-center gap-2 border-b border-slate-200 p-2">
        <div className="relative flex-1">
          <Search size={17} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={pole}
            className="input w-full pl-9"
            placeholder="Kód, číslo objednávky, firma, telefón…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoComplete="off"
            /* `search` namiesto `text`: na telefóne sa tým na klávesnici
             * objaví „Hľadať" namiesto „Ďalej". */
            type="search"
            enterKeyHint="search"
          />
        </div>
        <button className="rounded p-2 text-slate-500 active:bg-slate-100" onClick={onClose} title="Zavrieť">
          <X size={20} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.trim().length < 2 ? (
          <p className="p-4 text-sm text-slate-400">
            Napíšte aspoň dve písmená. Hľadá sa v objednávkach, ponukách, tovare,
            zákazníkoch, faktúrach aj v pošte – podľa toho, kam viete.
          </p>
        ) : hladam && !v ? (
          <p className="p-4 text-sm text-slate-400">Hľadám…</p>
        ) : v?.chyba ? (
          <p className="p-4 text-sm text-rose-700">Hľadanie sa nepodarilo. Skúste to znova.</p>
        ) : !v?.spolu ? (
          <p className="p-4 text-sm text-slate-500">
            Nenašlo sa nič na „{q.trim()}".
          </p>
        ) : (
          v.skupiny.map((s) => (
            <div key={s.kluc}>
              <div className="sticky top-0 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {s.nazov}
              </div>
              {s.polozky.map((p, i) => (
                <div key={`${s.kluc}-${i}`} className="flex items-center gap-1 border-b border-slate-100">
                  <button className="min-w-0 flex-1 px-3 py-2.5 text-left active:bg-slate-50"
                    onClick={() => chod(p.odkaz)}>
                    <div className="truncate font-medium text-slate-800">{p.nazov}</div>
                    {p.popis && <div className="truncate text-xs text-slate-500">{p.popis}</div>}
                  </button>
                  {/* Zákazník s telefónom – zavolať sa dá rovno odtiaľto.
                      Je to najčastejší dôvod, prečo ho niekto hľadá. */}
                  {p.telefon && (
                    <a href={`tel:${String(p.telefon).replace(/\s/g, '')}`}
                      className="shrink-0 rounded-full bg-slate-100 p-2 text-slate-700 active:bg-slate-200"
                      title={`Zavolať ${p.telefon}`}>
                      <Phone size={15} />
                    </a>
                  )}
                  <ChevronRight size={15} className="mr-2 shrink-0 text-slate-300" />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
